
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { useInView } from "react-intersection-observer";

import { colorToString } from '../widget.helper';
import { Widgets } from './../../../index';

import styles from './background.module.scss';
import { POSITION, SCALE } from '../../../constants/background';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';

export const BackgroundViewComponent = ({ className, background, style, breakpoint }) => {
  if (background == null) {
    return false;
  }

  const type = _.get(background, 'type');
  const color = _.get(background, 'color');
  const colorOverlay = getColorsConfig(color);

  return (
    <div className={cx(styles.background, className)} style={style}>
      <div className={cx(styles.overlay, styles.color)} style={{
        background: colorOverlay
      }}></div>

      {type === 'image' &&
        <ImageView background={background} />
      }

      {type === 'video' &&
        <VideoView background={background} breakpoint={breakpoint} />
      }
    </div>
  );
};

const backgroundViewEquals = function (prevProps, nextProps) {
  const paths = ['className', 'background', 'style'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const BackgroundView = React.memo(BackgroundViewComponent, backgroundViewEquals);

export const ImageViewComponent = ({ background }) => {
  const context = React.useContext(Widgets.Context);
  const media = _.get(background, 'media');
  if (!media) {
    return null;
  }

  const backgroundStyles = getImageBackgroundStyles(context.imageCDN, background);

  return (
    <div className={cx(styles.overlay, styles.image)} style={backgroundStyles} />
  );
}

const imageViewEquals = function (prevProps, nextProps) {
  const paths = ['background'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const ImageView = React.memo(ImageViewComponent, imageViewEquals);

export const VideoViewComponent = ({ background, breakpoint }) => {
  const context = React.useContext(Widgets.Context);
  const media = _.get(background, 'media');

  if (!media) {
    return null;
  }

  const videoRef = useRef(null);
  const { ref, inView } = useInView({
    threshold: 0.3
  });

  var url = `${media.url}`;
  const backgroundStyles = getVideoBackgroundStyles(background);

  useEffect(() => {
    if (videoRef.current) {
      if (inView) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [inView])

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    var videoUrl = `${context.imageCDN}/${url}`;
    if (breakpoint === 'mobile') {
      var height = videoRef.current.offsetHeight;
      var maxHeight = _.get(background, 'media.height') || 1080;

      if (height <= 360) {
        height = 360;
      } else if (height <= 480) {
        height = 480;
      } else if (height <= 720) {
        height = 720;
      } else {
        height = 1080;
      }

      height = Math.min(height, maxHeight);

      if (height >= 360) {
        videoUrl = `${videoUrl}?tr=h-${height}`;
      }
    }

    videoRef.current.src = videoUrl;

  }, [breakpoint]);


  return (
    <div ref={ref} className={cx(styles.overlay, styles.video)}>
      <video
        style={backgroundStyles}
        ref={videoRef}
        playsInline
        preload={'auto'}
        className={styles.videoPlayer}
        muted
        autoPlay
        loop
        crossOrigin='anonymous'
        poster={`${context.imageCDN}/${url}/ik-thumbnail.jpg`}
      />
    </div>
  );
}

const videoViewEquals = function (prevProps, nextProps) {
  const paths = ['background'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const VideoView = React.memo(VideoViewComponent, videoViewEquals);

function getImageBackgroundStyles(imageCDN, background) {
  var backgroundStyles = {};

  const media = _.get(background, 'media');
  var url = `${imageCDN}/${media.url}`;
  var scale = _.get(background, 'scale', SCALE.SCALE_TO_FILL);
  var opacity = _.get(background, 'opacity', 100) / 100;

  backgroundStyles = {
    ...backgroundStyles,
    backgroundImage: `url('${url}')`,
    opacity: opacity
  }

  switch (scale) {
    case SCALE.TILED:
      backgroundStyles = {
        ...backgroundStyles,
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto'
      }

      break;
    case SCALE.SCALE_TO_FILL:
    default:

      backgroundStyles = {
        ...backgroundStyles,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'
      }
      break;
  }

  const backgroundPosition = getBackgroundPosition(background);
  backgroundStyles = {
    ...backgroundStyles,
    backgroundPosition: backgroundPosition,
    height: '100%'
  }

  return backgroundStyles;
}

function getVideoBackgroundStyles(background) {
  var backgroundStyles = {};

  var opacity = _.get(background, 'opacity', 100) / 100;

  backgroundStyles = {
    ...backgroundStyles,
    opacity: opacity,
  }

  const backgroundPosition = getBackgroundPosition(background);
  backgroundStyles = {
    ...backgroundStyles,
    objectPosition: backgroundPosition,
  }

  return backgroundStyles;
}

function getBackgroundPosition(background) {
  var position = _.get(background, 'position', POSITION.CENTER);

  var backgroundPosition = 'center center';
  switch (position) {
    case POSITION.TOP_LEFT:
      backgroundPosition = 'left top';
      break;

    case POSITION.TOP_CENTER:
      backgroundPosition = 'center top';
      break;

    case POSITION.TOP_RIGHT:
      backgroundPosition = 'right top';
      break;

    case POSITION.CENTER_LEFT:
      backgroundPosition = 'left center';
      break;

    case POSITION.CENTER:
      backgroundPosition = 'center center';
      break;

    case POSITION.CENTER_RIGHT:
      backgroundPosition = 'right center';
      break;

    case POSITION.BOTTOM_LEFT:
      backgroundPosition = 'left bottom';
      break;

    case POSITION.BOTTOM_CENTER:
      backgroundPosition = 'center bottom';
      break;

    case POSITION.BOTTOM_RIGHT:
      backgroundPosition = 'right bottom';
      break;
  }

  return backgroundPosition;
}

export default BackgroundView;
